const EPSILON = 0.01;
const METHODES = ['ESPECES', 'CARTE', 'CHEQUE', 'VIREMENT'];

export function roundMoney(n) {
  return Math.round(Number(n) * 100) / 100;
}

export function montantFinal(montant, reduction = 0) {
  if (montant == null) return null;
  return Math.max(0, roundMoney(montant - (reduction || 0)));
}

/** Somme des lignes de paiement */
export function sommeLignes(lignes) {
  return roundMoney(
    (lignes || []).reduce((s, l) => s + (Number(l.montant) || 0), 0)
  );
}

export function resteAPayer(montantDu, lignes) {
  return roundMoney(montantDu - sommeLignes(lignes));
}

/** Regroupe les lignes par mode (pour l'API) */
export function consoliderPaiements(lignes) {
  const out = {};
  for (const l of lignes || []) {
    const m = l.methode;
    const v = roundMoney(l.montant);
    if (v > 0 && METHODES.includes(m)) {
      out[m] = roundMoney((out[m] || 0) + v);
    }
  }
  return out;
}

export function totalEspecesLignes(lignes) {
  return roundMoney(
    (lignes || [])
      .filter((l) => l.methode === 'ESPECES')
      .reduce((s, l) => s + (Number(l.montant) || 0), 0)
  );
}

export function calcRenduEspeces(montantRecu, lignes) {
  const especes = totalEspecesLignes(lignes);
  if (especes <= 0) return null;
  const recu = montantRecu !== '' && montantRecu != null ? roundMoney(montantRecu) : null;
  if (recu == null || recu < especes - EPSILON) return null;
  return roundMoney(recu - especes);
}

const LABELS = {
  ESPECES: 'Espèces',
  CARTE: 'Carte',
  CHEQUE: 'Chèque',
  VIREMENT: 'Virement',
};

export function labelMethode(m) {
  return LABELS[m] || m;
}

/**
 * Valide les lignes cumulées avant encaissement.
 * L1-T6 : `allowPartial` autorise une opération < montantDu (solde restant).
 */
export function validerLignesPaiement(montantDu, lignes, montantRecuEspeces, { allowPartial = false } = {}) {
  const total = roundMoney(montantDu);
  if (total <= 0) {
    return { ok: false, message: 'Montant invalide' };
  }

  if (!lignes?.length) {
    return { ok: false, message: 'Ajoutez au moins un paiement' };
  }

  const reste = resteAPayer(total, lignes);
  if (reste < -EPSILON) {
    return {
      ok: false,
      message: `Trop perçu de ${Math.abs(reste).toFixed(2)} €`,
      reste,
      paiements: consoliderPaiements(lignes),
    };
  }
  if (!allowPartial && reste > EPSILON) {
    return {
      ok: false,
      message: `Il reste ${reste.toFixed(2)} € à encaisser`,
      reste,
      paiements: consoliderPaiements(lignes),
    };
  }

  const paiements = consoliderPaiements(lignes);
  const especes = paiements.ESPECES || 0;
  let montantRecu = null;

  if (especes > 0) {
    const recuRaw =
      montantRecuEspeces !== '' && montantRecuEspeces != null
        ? montantRecuEspeces
        : especes;
    montantRecu = roundMoney(recuRaw);
    if (montantRecu < especes - EPSILON) {
      return {
        ok: false,
        message: `Espèces reçues insuffisantes (${montantRecu} € / ${especes.toFixed(2)} € attendus)`,
        paiements,
      };
    }
  }

  const methodePrincipale = Object.entries(paiements).sort((a, b) => b[1] - a[1])[0]?.[0] || 'ESPECES';
  const isPartial = reste > EPSILON;

  return {
    ok: true,
    paiements,
    methodePaiement: Object.keys(paiements).length > 1 ? 'MIXTE' : methodePrincipale,
    montantRecu,
    reste: Math.max(0, reste),
    isPartial,
    allowPartial: Boolean(allowPartial) && isPartial,
  };
}

/** @deprecated — utiliser validerLignesPaiement */
export function validerPaiementCaisse() {
  return { ok: false, message: 'Utilisez validerLignesPaiement' };
}
